import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { CheckboxModel } from '@fi-shared/checkbox';
import {
    IAccessRightFilterCollection,
    ILightWeightObject,
    ILightWeightUser,
    LocalSearchDataModel,
    LocalSearchItemModel,
    LocalSearchModel,
} from '@fi-shared/models';
import { ObjectTypes } from '@fi-shared/types';
import { AppTextService } from '@fi-shared/app-text';
import { Observable, Subject } from 'rxjs';
import { AccessRightService } from '@fi-shared/fi-shared-services';
import { takeUntil } from 'rxjs/operators';
import { NgModel } from '@angular/forms';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { IInitialSelectionFilterSettings } from '@fi-shared/access-right-filters/access-right-filters';
import {
    FiDropdownActionResult,
    FiDropdownItemDisabledFn,
    FiDropdownItemTitleFn,
} from '@fi-shared/fi-select-search/fi-select-search';
import {
    FiAccessRightHelperService,
    IAccessRightsCoreFiltersErrors,
} from '@fi-shared/fi-shared-services/fi-access-right-helper.service';
import { ModuleAccessLevel } from '@fi-shared/access-right-filters/module-access-level';

@Component({
    selector: 'fi-access-right-filters-manual-selection',
    templateUrl: './access-right-filters-manual-selection.component.html',
    styleUrls: ['./access-right-filters-manual-selection.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-access-right-filters-manual-selection' },
    providers: [{ provide: FiComponentsContainer, useExisting: AccessRightFiltersManualSelectionComponent }],
    standalone: false,
})
export class AccessRightFiltersManualSelectionComponent
    extends FiComponentsContainer
    implements OnInit, OnChanges, OnDestroy
{
    @Input() errors: IAccessRightsCoreFiltersErrors;
    @Input() usersOutOfScope: ILightWeightUser[] = [];
    @Input() clustersUnitsOutOfScope: ILightWeightObject[] = [];
    @Input() isSharedWithExtraColleaguesOrUnits: boolean;
    @Input('accessRightFilterCollection') ngModel: IAccessRightFilterCollection;
    @Input() CcmUserSearch: LocalSearchModel;
    @Input() createSelectionFilterModel: CheckboxModel;
    @Input() selectedSelectionFilterName: string;
    @Input() chooseUnitsColleaguesTextId: string;
    @Input() showUserSelect: boolean;
    @Input() isNewQuickSelectionFocus: boolean;
    @Input() HasAreaRights: boolean;
    @Input() initialSelectionFilterSettings: IInitialSelectionFilterSettings;
    @Input() disableUncheck: boolean;
    @Input() UserData: LocalSearchDataModel;
    @Input() BusinessUnitsData: LocalSearchDataModel;
    @Input() ClustersData: LocalSearchDataModel;
    @Input() initAccess$: Observable<IAccessRightFilterCollection> = new Observable<IAccessRightFilterCollection>();
    @Input() updateNotAvailableIds$: Observable<IAccessRightFilterCollection> =
        new Observable<IAccessRightFilterCollection>();
    @Input() CcmBusinessUnitAndClusterSearch: LocalSearchModel;
    @Output() onSetNewSelectionAsDefault = new EventEmitter<void>();

    @Input() selectedUsers: LocalSearchItemModel[];
    @Input() selectedBusinessUnitsAndClusters: LocalSearchItemModel[];

    @Output() selectedRolesChange = new EventEmitter<void>();
    @Output() selectedBusinessAreasChange = new EventEmitter<void>();
    @Output() selectedAreasChange = new EventEmitter<void>();
    @Output() selectedBusinessUnitsAndClustersChange = new EventEmitter<LocalSearchItemModel[]>();
    @Output() selectedUsersChange = new EventEmitter<LocalSearchItemModel[]>();

    @Output() selectedSelectionFilterNameChange = new EventEmitter<string>();
    @Output('accessRightFilterCollectionChange') ngModelChange = new EventEmitter<IAccessRightFilterCollection>();
    @ViewChild('newQuickSelectionInput', { static: true }) newQuickSelectionInput: ElementRef<HTMLElement>;
    @ViewChild('newQuickSelectionModel', { static: true }) newQuickSelectionModel: NgModel;

    private notAvailableClusterIds: number[];
    private notAvailableUserIds: number[];
    private deactivatedBusinessUnitIds: number[];
    private deactivatedUserIds: number[];
    private notAvailableBusinessUnitIds: number[];

    accessRightFilterCollection: IAccessRightFilterCollection; // contains all available roles, areas, business areas etc

    ObjectTypes = ObjectTypes;

    constructor(
        private appTextService: AppTextService,
        private accessRightService: AccessRightService,
        private fiAccessRightHelperService: FiAccessRightHelperService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.initAccess$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((accessRightFilterCollection: IAccessRightFilterCollection) => {
                this.accessRightFilterCollection = accessRightFilterCollection;
                this.checkSelectSingleBusinessArea();
            });
        this.updateNotAvailableIds$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((accessRightFilterCollection: IAccessRightFilterCollection) => {
                this.updateNotAvailableIds();
            });
    }

    private updateNotAvailableIds(): void {
        this.notAvailableBusinessUnitIds = this.accessRightService.AddReadOnlyItemsForSearchItems<ILightWeightObject>(
            this.accessRightFilterCollection.AccessRightCoreFilter.BusinessUnits,
            ObjectTypes.BusinessUnit,
            this.BusinessUnitsData,
            (item: ILightWeightObject) => item.Name,
        );

        this.deactivatedBusinessUnitIds = this.accessRightFilterCollection.AccessRightCoreFilter.BusinessUnits.filter(
            (bu) => !bu.IsActivated,
        ).map((bu) => bu.Id);

        this.notAvailableClusterIds = this.accessRightService.AddReadOnlyItemsForSearchItems<ILightWeightObject>(
            this.accessRightFilterCollection.AccessRightCoreFilter.Clusters,
            ObjectTypes.Cluster,
            this.ClustersData,
            (item: ILightWeightObject) => item.Name,
        );

        if (this.showUserSelect) {
            this.notAvailableUserIds = this.accessRightService.AddReadOnlyItemsForSearchItems<ILightWeightUser>(
                this.accessRightFilterCollection.AccessRightUserFilter.Users,
                ObjectTypes.User,
                this.UserData,
                (item: ILightWeightUser) => item.FullName,
            );

            this.deactivatedUserIds = this.accessRightFilterCollection.AccessRightUserFilter.Users.filter(
                (user) => !user.IsActivated,
            ).map((user) => user.Id);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isNewQuickSelectionFocus?.currentValue) {
            this.newQuickSelectionInput.nativeElement.focus();
        }
    }

    onSelectedSelectionFilterNameChange(): void {
        this.selectedSelectionFilterNameChange.emit(this.selectedSelectionFilterName);
    }

    canRemoveExtendedSharing($event: {
        item: LocalSearchItemModel;
        isSelected: boolean;
    }): Promise<FiDropdownActionResult> {
        const extendedSharingUnavailableItemsIds = this.getOutOfScopeExtendedSharingItemsIdsByObjectType(
            $event.item.ObjectType,
        );
        return this.fiAccessRightHelperService.canRemoveExtendedSharing(
            extendedSharingUnavailableItemsIds,
            $event.item,
        );
    }

    private systemItemsData: { [prop: number]: number[] } = {};
    private getSystemItemIdsByObjectType(objectType: ObjectTypes): number[] {
        if (!this.systemItemsData[objectType]) {
            if (objectType === ObjectTypes.User) {
                this.systemItemsData[objectType] = this.accessRightFilterCollection.AccessRightUserFilter.Users.filter(
                    (item) => item.IsSystemObject,
                ).map((item) => item.Id);
            } else if (objectType === ObjectTypes.BusinessUnit) {
                this.systemItemsData[objectType] =
                    this.accessRightFilterCollection.AccessRightCoreFilter.BusinessUnits.filter(
                        (item) => item.IsSystemObject,
                    ).map((item) => item.Id);
            } else if (objectType === ObjectTypes.Cluster) {
                this.systemItemsData[objectType] =
                    this.accessRightFilterCollection.AccessRightCoreFilter.Clusters.filter(
                        (item) => item.IsSystemObject,
                    ).map((item) => item.Id);
            } else {
                throw new Error('unknown objectType in getSystemItemIdsByObjectType: ' + objectType);
            }
        }
        return this.systemItemsData[objectType];
    }

    private outOfScopeItemsData: { [prop: number]: number[] } = {};
    private getOutOfScopeExtendedSharingItemsIdsByObjectType(objectType: ObjectTypes): number[] {
        if (!this.outOfScopeItemsData[objectType]) {
            if (objectType === ObjectTypes.User) {
                this.outOfScopeItemsData[objectType] = this.usersOutOfScope.map(
                    (notAvailableUser) => notAvailableUser.Id,
                );
            } else if (objectType === ObjectTypes.BusinessUnit || objectType === ObjectTypes.Cluster) {
                // todo should not we filter clustersUnitsOutOfScope by objectType ?
                this.outOfScopeItemsData[objectType] = this.clustersUnitsOutOfScope.map(
                    (notClusterUnit) => notClusterUnit.Id,
                );
            } else {
                throw new Error('unknown objectType in canChangeAccessRight: ' + objectType);
            }
        }
        return this.outOfScopeItemsData[objectType];
    }

    isSetNewSelectionAsDefaultDisabled(): boolean {
        return !this.selectedSelectionFilterName;
    }

    setNewSelectionAsDefault(): void {
        this.onSetNewSelectionAsDefault.emit();
    }

    onSelectedBusinessUnitsAndClustersChanged($event: LocalSearchItemModel[]): void {
        this.selectedBusinessUnitsAndClustersChange.emit(this.selectedBusinessUnitsAndClusters);

        this.checkSelectSingleBusinessArea();
    }

    onSelectedUsersChanged($event: LocalSearchItemModel[]): void {
        this.selectedUsersChange.emit(this.selectedUsers);
    }

    onSelectedRolesChanged(): void {
        this.selectedRolesChange.emit();
        this.checkSelectSingleBusinessArea();
    }

    onSelectedBusinessAreasChanged(): void {
        this.selectedBusinessAreasChange.emit();
    }

    onSelectedAreasChanged(): void {
        this.selectedAreasChange.emit();
        this.checkSelectSingleBusinessArea();
    }

    private checkSelectSingleBusinessArea() {
        this.fiAccessRightHelperService.checkSelectSingleBusinessArea(
            this.accessRightFilterCollection.AccessRightCoreFilter,
            this.ngModel.AccessRightCoreFilter,
            () => {
                this.onSelectedBusinessAreasChanged();
            },
        );
    }

    isBusinessUnitDisabledFn: FiDropdownItemDisabledFn<LocalSearchItemModel> = (
        item: LocalSearchItemModel,
        allItems: LocalSearchItemModel[],
    ) => {
        return this.fiAccessRightHelperService.isBusinessUnitDisabled(
            item.Id,
            this.notAvailableBusinessUnitIds,
            this.deactivatedBusinessUnitIds,
        );
    };

    getExtendedSharingItemClassNameFn = (item: LocalSearchItemModel): { [key: string]: boolean } => {
        const outOfScopeItemIds = this.getOutOfScopeExtendedSharingItemsIdsByObjectType(item.ObjectType);
        const systemItemIds = this.getSystemItemIdsByObjectType(item.ObjectType);
        return this.fiAccessRightHelperService.getExtendedSharingItemClassName(
            item.Id,
            outOfScopeItemIds,
            systemItemIds,
        );
    };

    isUserDisabledFn: FiDropdownItemDisabledFn<LocalSearchItemModel> = (
        item: LocalSearchItemModel,
        allItems: LocalSearchItemModel[],
    ) => {
        return this.fiAccessRightHelperService.isUserDisabled(
            item.Id,
            this.notAvailableUserIds,
            this.deactivatedUserIds,
        );
    };

    getUserTitleFn: FiDropdownItemTitleFn<LocalSearchItemModel> = (
        item: LocalSearchItemModel,
        allItems: LocalSearchItemModel[],
    ) => {
        return this.fiAccessRightHelperService.getUserTitle(item.Id, this.deactivatedUserIds);
    };

    getBusinessUnitTitleFn: FiDropdownItemTitleFn<LocalSearchItemModel> = (
        item: LocalSearchItemModel,
        allItems: LocalSearchItemModel[],
    ) => {
        return this.fiAccessRightHelperService.getBusinessUnitTitle(item.Id, this.deactivatedBusinessUnitIds);
    };

    getAreaErrorText(): string {
        if (this.errors?.area_or_cluster_required) {
            return this.appTextService.get('access_rights.area_cluster_required');
        }
        if (this.errors?.area_or_unit_or_cluster_required) {
            return this.appTextService.get('access_rights.area_cluster_unit_required');
        }
    }

    isBusinessAreasSelectionAvailable(): boolean {
        return this.fiAccessRightHelperService.isBusinessAreasSelectionAvailable(
            this.accessRightFilterCollection.AccessRightCoreFilter,
            this.ngModel.AccessRightCoreFilter,
        );
    }

    private isClusterSelectAvailable(): boolean {
        return !!this.ClustersData.Data.filter((item) => {
            return !this.notAvailableClusterIds.includes(item.Id);
        })?.length;
    }

    protected isSharingSelectionInfoShown(): boolean {
        return this.isBusinessAreasSelectionAvailable() || this.HasAreaRights || this.isClusterSelectAvailable();
    }

    private ngOnDestroy$ = new Subject<void>();

    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
